import IframeMessenger from '../Utility/IframeMessenger';
import PlayerActions from './enums/PlayerActions';
import PlayerData from './enums/PlayerData';
import _ from 'underscore';
import { PlayerAbstract } from './PlayerAbstract';
import { IframePostMessage, IframeRequestPromise, PlayerResponseData } from './types';
import { Log } from '../AuxillaryService/Log';
import { Deferred } from '../Utility/Deferred';
import { GenericConstructor } from '../Utility/Mixin';

/**
 * Player class that includes iframe messaging
 */
class IframeMessengerPlayerAbstract extends PlayerAbstract {
    /**
     * Stores requests sent to parent iframe
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly _promises: IframeRequestPromise<any>[] = [];

    /**
     * Facilitate communication with parent iframe
     */
    private readonly _messenger: IframeMessenger;

    /**
     * Keeps track of message id to prevent collision
     */
    static messageIdCounter = 0;

    /**
     * Store boot time to prevent collision with message id in the platform
     * layer. Collision is possible if the signs layer refreshes but
     * the platform does not refresh and persists the same message id list
     */
    static bootTime = Date.now().toString().padStart(14, '0');

    /**
     *Creates an instance of IframeMessengerPlayerAbstract.
     * @param {string} targetOrigin Domain that will create iframe for signs
     */
    constructor(targetOrigin: string) {
        super();

        // Handles sending and receiving messages from/to parent iframe
        // PWA player is hosted at webapp.reachcm.com
        this._messenger = new IframeMessenger(window.parent, targetOrigin);
        this._messenger.setMessageEventListener(this._parentIframeMessageHandler.bind(this));
    }

    /**
     * Handler for any message coming from parent Iframe
     */
    protected _parentIframeMessageHandler(message: IframePostMessage | unknown): void {
        const { id, action, data } = message as IframePostMessage;
        if (!message) {
            return;
        }
        const promiseIndex = _.findLastIndex(this._promises, { id: id });
        if (promiseIndex === -1) {
            throw new Error(`Cannot resolve. Promise not found for ${action}`);
        }

        Log.Debug('Message received from parent frame: ', null, __fileline, message);

        if (!action) {
            throw new Error("'action' should be present in the message.");
        }

        switch (action) {
            case PlayerActions.GET_SCREENSHOT:
                this._promises[promiseIndex].promise.resolve({
                    //remove the data type part because CMS adds it
                    base64Image: (data as string).replace('data:image/jpeg;base64,', ''),
                });
                break;
            case PlayerActions.GET_PLAYER_VERSION:
            case PlayerActions.GET_NAMED_VALUE:
            case PlayerActions.SET_NAMED_VALUE:
            case PlayerActions.DELETE_NAMED_VALUE:
            case PlayerActions.CONFIG_KEY_EXISTS:
            case PlayerActions.GET_NETWORK_STATUS:
            case PlayerActions.GET_DEVICE_INFORMATION:
            case PlayerActions.PLAYER_ACTION:
            case PlayerData.GET_DATA:
            case PlayerData.GET_LOGS:
                if (data) {
                    this._promises[promiseIndex].promise.resolve(data);
                } else {
                    this._promises[promiseIndex].promise.resolve(undefined);
                }
                break;
            default:
                throw new Error("'action' not recognized.");
        }

        this._promises.splice(promiseIndex, 1);
    }

    /**
     * Creates request promise that sends message to platform (PWA) layer and
     * pushes it to promises array. The promise is resolved once the platform
     * layer sends a message back
     */
    protected _invokePlayerMethod<TResponse extends PlayerResponseData>(
        action: PlayerActions | PlayerData,
        options: Record<string | number, unknown> = {}
    ): Deferred<TResponse> {
        const getDataPromise = new Deferred<TResponse>();
        const id = this._getMessageId();
        this._messenger.post(
            Object.assign(
                {
                    action: action,
                    id: id,
                },
                options
            )
        );

        this._promises.push({
            id: id,
            promise: getDataPromise,
        });

        return getDataPromise;
    }

    /**
     * Gets data from player
     * Use this function if the goal is to get some data from the player
     * E.g. Get logs
     */
    protected _getData<TResponse extends PlayerResponseData>(
        type: PlayerData
    ): Deferred<TResponse> {
        return this._invokePlayerMethod<TResponse>(PlayerData.GET_DATA, { type });
    }

    /**
     * Performs a player action
     * Use this function if the goal is to perform an action on the player
     * E.g. Reboot
     */
    protected _performPlayerAction<TResponse extends PlayerResponseData>(
        type: PlayerActions
    ): Deferred<TResponse> {
        return this._invokePlayerMethod<TResponse>(PlayerActions.PLAYER_ACTION, { type });
    }

    /**
     * Generates unique id that is used to identify request/response messages,
     * so that multiple requests can be sent
     */
    private _getMessageId(): number {
        return parseInt(
            (++IframeMessengerPlayerAbstract.messageIdCounter).toString() +
                IframeMessengerPlayerAbstract.bootTime
        );
    }
}

export type IframeMessengerable = GenericConstructor<IframeMessengerPlayerAbstract>;

export { IframeMessengerPlayerAbstract };
